import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../common/modal/Modal'
import { Button } from '@fluentui/react-components'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { t } from '../../i18n/i18n'
import { useForm } from 'react-hook-form'
import { FinancialYear, FinancialYearStatus } from '../../api/schemas/schema'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'
import { ControlledMoneyInput } from '../common/inputs/ControlledMoneyInput'
import { ControlledDropDown } from '../common/inputs/ControlledDropdown'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { DataClassification } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { IItemState } from '../../interfaces/IItemState'
import { updateFinancialYear } from '../../helpers/riskEconomyHelper'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { ControlledWorkplaceDropdownMulti } from '../common/inputs/ControlledWorkplaceDropdownMulti'

type ModalEditFinancialYearProps = {
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
   financialYearState: IItemState<FinancialYear>
   open: boolean
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const ModalEditFinancialYear = ({ setFinancialYearState, financialYearState, open, setOpen }: ModalEditFinancialYearProps) => {
   const dataContext = useContext(DataContext)
   const financialYear = financialYearState.item
   const disabled = financialYearState.disabled || financialYear.status !== FinancialYearStatus.Ongoing
   const {
      control,
      setValue,
      watch,
      reset,
      handleSubmit,
      formState: { isDirty, isSubmitting },
   } = useForm<FinancialYear>({ disabled: disabled })

   const onSubmit = async (data: FinancialYear) => {
      await updateFinancialYear(setFinancialYearState, dataContext, data)

      reset(financialYear)
      setOpen(false)
   }

   const close = () => {
      reset()
      setOpen(false)
   }

   useEffect(() => {
      reset(financialYear)
   }, [financialYear, reset])

   return (
      <>
         <DraggableModal open={open} title={t('EditItem', { Item: t('FinancialYear').toLowerCase() })} onClose={close}>
            <Form onSubmit={handleSubmit(onSubmit)}>
               <Grid>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('FinancialYear')} control={control} name="name" required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker
                        label={t('StartDate')}
                        control={control}
                        name="startDate"
                        required
                        tooltipMsg={t('ValueCannotBeChanged')}
                        showTooltip
                        isDisabled
                     />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker
                        label={t('EndDate')}
                        control={control}
                        name="endDate"
                        required
                        tooltipMsg={t('ValueCannotBeChanged')}
                        showTooltip
                        isDisabled
                     />
                  </GridItem>
                  {/* TODO: Enable workplace selection when multi-workplace support is fully implemented */}
                  {/* <GridItem size="12/12">
                     <ControlledWorkplaceDropdownMulti
                        label={t('Workplace')}
                        control={control}
                        name="workplaces"
                        required
                        tooltipMsg={t('ValueCannotBeChanged')}
                        showTooltip
                        disabled
                        options={[]}
                     />
                  </GridItem> */}
                  <GridItem size="6/12">
                     <ControlledAccountPicker label={t('Owner')} control={control} name="owner" watch={watch} setValue={setValue} required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDropDown
                        control={control}
                        name={'dataClassification'}
                        label={t('InformationManagement')}
                        options={enumValuesToOptions(DataClassification)}
                        required
                     />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledMoneyInput label={t('BudgetedRevenue')} control={control} name="budgetedRevenue" />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('Comment')} control={control} name="comment" multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ModalActions>
                        <Button onClick={close} disabled={isSubmitting}>
                           {t('Cancel')}
                        </Button>
                        {!disabled && (
                           <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                              {t('Save')}
                           </Button>
                        )}
                     </ModalActions>
                  </GridItem>
               </Grid>
            </Form>
         </DraggableModal>
      </>
   )
}
